import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Select, Tag, Space, Drawer, Descriptions, message, Form, Typography, Modal, Image, Spin, Popconfirm } from 'antd';
import { SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, PlayCircleOutlined, CheckOutlined, RollbackOutlined, ReloadOutlined, CommentOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { appealApi, alarmApi, customerApi } from '../services/api';
import { fmtCN } from '../utils/time';
import { actionText, logValueText } from '../utils/logLabels';
import { parseContent, cleanLogRemark } from '../utils/logRender';
import OrderAlarmBell from '../components/OrderAlarmBell';

const { Text, Paragraph } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'pending', label: '申诉待跟进' },
  { value: 'processing', label: '申诉处理中' },
  { value: 'awaiting_customer', label: '待客服处理' },
  { value: 'completed', label: '申诉完成' },
  { value: 'urgent', label: '紧急' },
];
const STATUS_COLOR = { pending: 'gold', processing: 'blue', awaiting_customer: 'purple', completed: 'green' };
const STATUS_TEXT = { pending: '申诉待跟进', processing: '申诉处理中', awaiting_customer: '待客服处理', completed: '申诉完成' };
const PRIORITY_COLOR = { urgent: 'red', high: 'orange', normal: 'blue', low: 'default' };
const REASON_TEXT = {
  dimension_weight: '尺寸复量', weight_diff: '重量差异', extra_service: '额外服务',
  amount_diff: '收费金额差异', duplicate_charge: '重复收费', other: '其他',
};

const REASON_OPTIONS = [
  { value: 'dimension_weight', label: '尺寸复量' },
  { value: 'weight_diff', label: '重量差异' },
  { value: 'extra_service', label: '额外服务' },
  { value: 'amount_diff', label: '收费金额差异' },
  { value: 'duplicate_charge', label: '重复收费' },
  { value: 'other', label: '其他' },
];

export default function AppealList({
  defaultStatus = '',
  title = '申诉工单',
  desc = '账单申诉独立工单，与调度工单完全隔离；按「申诉待跟进→申诉处理中→待客服处理→申诉完成」循环流转',
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || defaultStatus);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyImages, setReplyImages] = useState([]);
  const [replyUploading, setReplyUploading] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  // 流转相关弹窗
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultForm] = Form.useForm();
  const [backModalOpen, setBackModalOpen] = useState(false);
  const [backForm] = Form.useForm();
  const [custReplyOpen, setCustReplyOpen] = useState(false);
  const [custReplyForm] = Form.useForm();
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenForm] = Form.useForm();
  const [current, setCurrent] = useState(null);
  // 开始处理弹窗（对齐调度工单：实际操作内容必填 + 结果反馈可选）
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [startForm] = Form.useForm();
  const [startSubmitting, setStartSubmitting] = useState(false);

  // 修改客户
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerEditSaving, setCustomerEditSaving] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [userRole, setUserRole] = useState('');
  const customerSelectable = userRole === 'admin' || userRole === 'customer_service';

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      setUserRole(u.role || '');
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!customerSelectable) return;
    (async () => {
      try {
        const names = await customerApi.listNames();
        setCustomerList(Array.isArray(names) ? names : []);
      } catch (e) {}
    })();
  }, [customerSelectable]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * pageSize, limit: pageSize };
      if (keyword) params.keyword = keyword;
      if (statusFilter && statusFilter !== 'urgent') params.status = statusFilter;
      if (statusFilter === 'urgent') params.priority = 'urgent';
      const data = await appealApi.list(params);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter, keyword]);

  // 当前用户已设闹钟的申诉工单ID集合（铃铛高亮用；设置/取消后由 OrderAlarmBell 回调刷新）
  const [alarmIds, setAlarmIds] = useState(new Set());
  const fetchAlarmIds = async () => {
    try {
      const d = await alarmApi.mine('appeal');
      setAlarmIds(new Set(d.order_ids || []));
    } catch (e) {
      /* 拦截器已提示 */
    }
  };
  useEffect(() => {
    fetchAlarmIds();
  }, []);

  // 外部页面携带 appeal_id 时自动打开详情
  useEffect(() => {
    const aid = searchParams.get('appeal_id');
    if (aid) {
      openDetail({ id: parseInt(aid, 10) });
      const next = {};
      for (const [k, v] of searchParams.entries()) if (k !== 'appeal_id') next[k] = v;
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
    const next = {};
    if (keyword) next.keyword = keyword;
    if (statusFilter) next.status = statusFilter;
    setSearchParams(next, { replace: true });
  };

  const refreshDetail = async (id) => {
    if (detail?.id === id) {
      try {
        const fresh = await appealApi.get(id);
        setDetail(fresh);
      } catch (e) {}
    }
  };

  const openDetail = async (item) => {
    setDetail(item); // 先立即打开，再拉最新数据（含日志）
    try {
      const fresh = await appealApi.get(item.id);
      setDetail(fresh);
      // 客服打开待客服处理工单 → 标记已读（清除蓝点）
      if (fresh?.status === 'awaiting_customer' && fresh?.customer_unread) {
        try {
          const read = await appealApi.customerRead(item.id);
          setDetail(read);
          fetchData();
        } catch (e) {}
      }
    } catch (e) {}
  };

  // 待跟进 → 处理中：打开「开始处理」弹窗（对齐调度工单）
  const openStart = (item) => {
    setCurrent(item);
    startForm.resetFields();
    setStartModalOpen(true);
  };

  const handleStartSubmit = async (values) => {
    setStartSubmitting(true);
    try {
      // 先流转状态（待跟进 → 处理中），保证必定流转
      await appealApi.changeStatus(current.id, 'processing');
      // 再落实际操作内容；结果反馈为空时不覆盖已有内容（客服回复会写入该字段）
      const payload = { actual_action: (values.actual_action || '').trim() };
      if ((values.result_feedback || '').trim()) payload.result_feedback = values.result_feedback.trim();
      try {
        await appealApi.update(current.id, payload);
      } catch (e) {}
      message.success('操作记录已保存，工单已流转到「申诉处理中工单」');
      setStartModalOpen(false);
      fetchData();
      refreshDetail(current.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '操作失败');
    } finally {
      setStartSubmitting(false);
    }
  };

  // 是否已在编辑页填好「实际操作记录 + 结果反馈」（与流转弹窗同源字段）
  const hasPrefilledResult = (item) =>
    Boolean((item?.result_feedback || '').trim()) && Boolean((item?.actual_action || '').trim());

  // 编辑页已填写 → 直接以已存字段流转，无需重复弹出填写框
  const transferWithSavedResult = async (item) => {
    try {
      await appealApi.toAwaitingCustomer(item.id, {
        result_note: (item.result_feedback || '').trim(),
        actual_action: (item.actual_action || '').trim(),
      });
      message.success('已流转至待客服处理工单');
      fetchData();
      refreshDetail(item.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  const openResultModal = (item) => {
    setCurrent(item);
    resultForm.resetFields();
    setResultModalOpen(true);
  };

  const handleToAwaiting = async (values) => {
    try {
      await appealApi.toAwaitingCustomer(current.id, {
        result_note: values.result_note,
        actual_action: values.actual_action || '',
      });
      message.success('已流转至待客服处理工单');
      setResultModalOpen(false);
      fetchData();
      refreshDetail(current.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  // 处理中 → 退回待跟进（须填写进展内容）
  const openBackToPending = (item) => {
    setCurrent(item);
    backForm.resetFields();
    setBackModalOpen(true);
  };

  const handleBackToPending = async (values) => {
    try {
      await appealApi.backToPending(current.id, { progress_note: values.progress_note });
      message.success('已退回申诉待跟进');
      setBackModalOpen(false);
      fetchData();
      refreshDetail(current.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  // 结束工单
  const handleComplete = async (item) => {
    try {
      await appealApi.complete(item.id);
      message.success('工单已结束');
      fetchData();
      refreshDetail(item.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  // 待客服处理 → 客服回复（回到申诉待跟进）
  const openCustomerReply = (item) => {
    setCurrent(item);
    custReplyForm.resetFields();
    setCustReplyOpen(true);
  };

  const handleCustomerReply = async (values) => {
    try {
      await appealApi.customerReply(current.id, { reply_note: values.reply_note });
      message.success('客服回复已提交，工单回到申诉待跟进');
      setCustReplyOpen(false);
      fetchData();
      refreshDetail(current.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  // 完成 → 重新打开（须填写新的工单需求）
  const openReopen = (item) => {
    setCurrent(item);
    reopenForm.resetFields();
    setReopenOpen(true);
  };

  // 修改客户
  const openCustomerModal = (item) => {
    setCurrent(item);
    setSelectedCustomer(item.customer_name || null);
    setCustomerModalOpen(true);
  };
  const handleCustomerSave = async () => {
    if (!current || !customerSelectable) return;
    const value = (selectedCustomer || '').trim();
    if (!value) {
      message.error('请选择客户');
      return;
    }
    setCustomerEditSaving(true);
    try {
      await appealApi.updateCustomer(current.id, value);
      message.success('客户已更新');
      setCustomerModalOpen(false);
      fetchData();
      refreshDetail(current.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '更新失败');
    } finally {
      setCustomerEditSaving(false);
    }
  };

  const handleReopenSubmit = async (values) => {
    try {
      await appealApi.reopen(current.id, { new_issue: values.new_issue });
      message.success('工单已重新打开，回到申诉待跟进');
      setReopenOpen(false);
      fetchData();
      refreshDetail(current.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  const handleReplyPaste = async (e) => {
    const files = [];
    for (const it of e.clipboardData?.items || []) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (!files.length) return;
    e.preventDefault();
    setReplyUploading(true);
    try {
      const newAttachments = [];
      for (const f of files) {
        const d = await appealApi.uploadFile(f);
        newAttachments.push({ url: d.url, name: d.name || f.name, isImage: f.type.startsWith('image/') });
      }
      setReplyImages((prev) => [...prev, ...newAttachments]);
      message.success(`已粘贴 ${files.length} 个文件`);
    } catch (err) {
      message.error(err?.response?.data?.detail || '文件上传失败');
    } finally {
      setReplyUploading(false);
    }
  };

  const removeReplyImage = (url) => {
    setReplyImages((prev) => prev.filter((a) => a.url !== url));
  };

  const handleReplySubmit = async () => {
    if ((!replyContent.trim() && !replyImages.length) || !detail) return;
    setReplySubmitting(true);
    try {
      const attachMd = replyImages.map((a) =>
        a.isImage ? `![${a.name}](${a.url})` : `[${a.name}](${a.url})`
      ).join('\n');
      const content = [replyContent.trim(), attachMd].filter(Boolean).join('\n');
      await appealApi.addChat(detail.id, content);
      message.success('回复已提交');
      setReplyOpen(false);
      setReplyContent('');
      setReplyImages([]);
      const fresh = await appealApi.get(detail.id);
      setDetail(fresh);
      fetchData();
    } catch (e) {
      message.error(e?.response?.data?.detail || '回复失败');
    } finally {
      setReplySubmitting(false);
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    editForm.setFieldsValue({
      appeal_reason: item.appeal_reason,
      appeal_amount: item.appeal_amount,
      customer_name: item.customer_name,
      customer_owner: item.customer_owner,
      expected_result: item.expected_result,
      channel_party: item.channel_party,
      follow_strategy: item.follow_strategy,
      escalate_hours: item.escalate_hours,
      actual_action: item.actual_action,
      result_feedback: item.result_feedback,
    });
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      await appealApi.update(editing.id, values);
      message.success('更新成功');
      setEditing(null);
      if (detail?.id === editing.id) {
        const fresh = await appealApi.get(editing.id);
        setDetail(fresh);
      }
      fetchData();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || '保存失败');
    } finally {
      setEditSaving(false);
    }
  };

  // 状态标签（待客服处理未读时加蓝点）
  const renderStatus = (item, v) => (
    <Space size={4}>
      {item?.status === 'awaiting_customer' && item?.customer_unread && (
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#1677ff' }} title="客服未读" />
      )}
      <Tag color={STATUS_COLOR[v]}>{STATUS_TEXT[v] || v}</Tag>
    </Space>
  );

  // 操作列按钮（对齐调度工单流转）
  const renderActions = (item) => (
    <Space direction="vertical" size={6}>
      <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(item)}>详情</Button>
      {customerSelectable && (
        <Button size="small" onClick={() => openCustomerModal(item)}>
          {item.customer_name ? '修改客户' : '添加客户'}
        </Button>
      )}
      {item.status !== 'pending' && item.status !== 'processing' && (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(item)}>编辑</Button>
      )}
      {item.status === 'pending' && (
        item.need_result ? (
          <Button size="small" type="primary" style={{ background: '#722ed1', borderColor: '#722ed1' }} icon={<PlayCircleOutlined />} onClick={() => openResultModal(item)}>补填结果转客服</Button>
        ) : (
          <Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={() => openStart(item)}>开始处理</Button>
        )
      )}
      {item.status === 'processing' && (
        hasPrefilledResult(item) ? (
          <Popconfirm
            title="将以编辑页已填写的「实际操作记录」「结果反馈」直接流转至待客服，是否继续？"
            okText="直接流转" cancelText="取消"
            onConfirm={() => transferWithSavedResult(item)}
          >
            <Button size="small" style={{ color: '#52c41a', borderColor: '#52c41a' }} icon={<CheckOutlined />}>流转待客服</Button>
          </Popconfirm>
        ) : (
          <Button size="small" style={{ color: '#52c41a', borderColor: '#52c41a' }} icon={<CheckOutlined />} onClick={() => openResultModal(item)}>流转待客服</Button>
        )
      )}
      {item.status === 'processing' && (
        <Button size="small" icon={<RollbackOutlined />} onClick={() => openBackToPending(item)}>回退待跟进</Button>
      )}
      {item.status === 'awaiting_customer' && (
        <Button size="small" type="primary" icon={<CommentOutlined />} onClick={() => openCustomerReply(item)}>客服回复</Button>
      )}
      {item.status !== 'completed' && (
        <Popconfirm title="确认结束此申诉工单？" onConfirm={() => handleComplete(item)} okText="确认" cancelText="取消">
          <Button size="small" danger icon={<CheckCircleOutlined />}>结束工单</Button>
        </Popconfirm>
      )}
      {item.status === 'completed' && (
        <Button size="small" icon={<ReloadOutlined />} onClick={() => openReopen(item)}>重新打开</Button>
      )}
    </Space>
  );

  // 详情弹窗内按钮（不含「详情」；处理中也可编辑以补填两项后直接流转）
  const renderDetailActions = (item) => (
    <Space size={6} wrap>
      {customerSelectable && (
        <Button size="small" onClick={() => openCustomerModal(item)}>
          {item.customer_name ? '修改客户' : '添加客户'}
        </Button>
      )}
      {item.status !== 'completed' && (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(item)}>编辑</Button>
      )}
      {item.status === 'processing' && (
        hasPrefilledResult(item) ? (
          <Button size="small" style={{ color: '#52c41a', borderColor: '#52c41a' }} icon={<CheckOutlined />} onClick={() => transferWithSavedResult(item)}>流转待客服</Button>
        ) : (
          <Button size="small" style={{ color: '#52c41a', borderColor: '#52c41a' }} icon={<CheckOutlined />} onClick={() => openResultModal(item)}>流转待客服</Button>
        )
      )}
      {item.status === 'awaiting_customer' && (
        <Button size="small" type="primary" icon={<CommentOutlined />} onClick={() => openCustomerReply(item)}>客服回复</Button>
      )}
    </Space>
  );

  const columns = [
    {
      title: '申诉工单号',
      dataIndex: 'appeal_no',
      width: 185,
      render: (v, item) => (
        <Space size={4}>
          <Text copyable strong>{v}</Text>
          <OrderAlarmBell orderType="appeal" orderId={item.id} active={alarmIds.has(item.id)} onChanged={fetchAlarmIds} />
        </Space>
      ),
    },
    { title: '科维斯订单号', dataIndex: 'kewell_order_no', width: 150, render: (v) => v || '-' },
    { title: '客户', dataIndex: 'customer_name', width: 130, render: (v) => v || '-' },
    {
      title: '申诉原因',
      dataIndex: 'appeal_reason',
      width: 130,
      render: (v) => <Tag>{REASON_TEXT[v] || v || '-'}</Tag>,
    },
    { title: '申诉金额', dataIndex: 'appeal_amount', width: 110, render: (v) => v || '-' },
    { title: '渠道方', dataIndex: 'channel', width: 140, ellipsis: true, render: (v) => v || '-' },
    {
      title: '关联返账单',
      width: 140,
      render: (v, item) => item.has_rebill_link ? (
        <Tag color="red" style={{ fontWeight: 'bold' }}>有返账单</Tag>
      ) : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      render: (v, item) => renderStatus(item, v),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      width: 90,
      render: (v) => v ? <Tag color={PRIORITY_COLOR[v]}>{v === 'urgent' ? '紧急' : v === 'high' ? '高' : '普通'}</Tag> : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 150,
      render: (v) => v ? fmtCN(v) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_, item) => renderActions(item),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>{title}</h2>
        <p className="desc">{desc}</p>
      </div>

      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="搜索申诉工单号 / 科维斯订单号 / 客户 / 渠道方"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 360 }}
            allowClear
          />
          <Select
            style={{ width: 140 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={() => {
            setKeyword(''); setStatusFilter(''); setPage(1);
            setSearchParams({}, { replace: true });
            fetchData();
          }}>重置</Button>
        </Space>
      </Card>

      <Card className="card-shadow">
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1350 }}
          locale={{ emptyText: '暂无申诉工单' }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true,
            showQuickJumper: true, showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      {/* 申诉详情 Modal（居中显示） */}
      <Modal
        title={`申诉工单 ${detail?.appeal_no || ''}`}
        open={!!detail}
        onCancel={() => setDetail(null)}
        width={720}
        centered
        footer={null}
        destroyOnClose
        extra={detail ? renderDetailActions(detail) : null}
      >
        {detail && (
          <>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="申诉工单号" span={2}><Text copyable>{detail.appeal_no}</Text></Descriptions.Item>
              <Descriptions.Item label="科维斯订单号">{detail.kewell_order_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="查询编码">dispute {detail.kewell_track || '-'}</Descriptions.Item>
              <Descriptions.Item label="申诉原因">{REASON_TEXT[detail.appeal_reason] || detail.appeal_reason}</Descriptions.Item>
              <Descriptions.Item label="申诉金额">{detail.appeal_amount || '-'}</Descriptions.Item>
              <Descriptions.Item label="客户">{detail.customer_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="客户归属">{detail.customer_owner || '-'}</Descriptions.Item>
              <Descriptions.Item label="渠道方">{detail.channel_party || detail.channel || '-'}</Descriptions.Item>
              <Descriptions.Item label="原账单金额">{detail.original_amount || '-'}</Descriptions.Item>
              <Descriptions.Item label="预期结果" span={2}>{detail.expected_result || '-'}</Descriptions.Item>
              <Descriptions.Item label="遵循系统规则">{detail.follow_strategy === 'yes' ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="升级周期">{detail.escalate_hours || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_COLOR[detail.status]}>{STATUS_TEXT[detail.status] || detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">{fmtCN(detail.created_at)}</Descriptions.Item>
              <Descriptions.Item label="实际操作记录" span={2}>{detail.actual_action || '-'}</Descriptions.Item>
              <Descriptions.Item label="处理结果反馈" span={2}>{detail.result_feedback || '-'}</Descriptions.Item>
            </Descriptions>

            <Card size="small" type="inner" title="申诉资料正文" style={{ marginBottom: 12 }}>
              {detail.appeal_detail ? (
                <div style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{parseContent(detail.appeal_detail)}</div>
              ) : (
                <Paragraph type="secondary">(无)</Paragraph>
              )}
            </Card>

            <Card size="small" type="inner" title="历史日志" extra={<Button type="primary" size="small" onClick={() => { setReplyContent(''); setReplyImages([]); setReplyOpen(true); }}>回复</Button>}>
              {detail.logs && detail.logs.length ? (
                [...detail.logs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((log) => (
                  <div key={log.id} style={{ borderBottom: '1px dashed #f0f0f0', padding: '8px 0' }}>
                    <div style={{ color: '#999', fontSize: 12 }}>
                      {fmtCN(log.created_at, 'YYYY-MM-DD HH:mm:ss')} · {log.operator_name || '系统'} · {actionText(log.action)}
                    </div>
                    {log.content && (
                      <div style={{ margin: '4px 0 0 0', whiteSpace: 'pre-wrap', background: '#fafafa', padding: 8, borderRadius: 4 }}>{parseContent(logValueText(cleanLogRemark(log.content)))}</div>
                    )}
                  </div>
                ))
              ) : <Text type="secondary">暂无日志</Text>}
            </Card>

            {/* 关联返账单清单：后端 /api/appeals/{id} 已返 rebill_list；在此抽屉内直接渲染 */}
            {(detail.rebill_list && detail.rebill_list.length) ? (
              <Card size="small" type="inner" title={`关联返账单清单 (${detail.rebill_list.length})`}
                    style={{ marginTop: 12, borderLeft: '3px solid #ff4d4f' }}>
                <div style={{ maxHeight: 260, overflow: 'auto' }}>
                  {detail.rebill_list.map((rb) => (
                    <div key={rb.rebill_id}
                         style={{
                           padding: '8px 10px',
                           borderBottom: '1px dashed #f0f0f0',
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: 'center',
                           gap: 12,
                         }}>
                      <Space direction="vertical" size={2}>
                        <Space>
                          <Text strong>{rb.rebill_no}</Text>
                          <Tag color={rb.status === 'closed' ? 'default' : rb.status === 'awaiting_cs' ? 'red' : 'orange'}>
                            {rb.status_label || rb.status}
                          </Tag>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {rb.amount != null ? `金额 ¥${rb.amount}` : ''}
                          {rb.bearing_type ? ` · 承担=${rb.bearing_type}` : ''}
                          {rb.cs_reply ? ` · CS回复：${(rb.cs_reply || '').slice(0, 80)}` : ''}
                        </Text>
                      </Space>
                      <a href={`/rebills?highlight=${rb.rebill_id}`} target="_blank" rel="noreferrer"
                         style={{ fontSize: 12 }}>查看 →</a>
                    </div>
                  ))}
                </div>
              </Card>
            ) : detail.has_rebill_link ? (
              <Card size="small" type="inner" title="关联返账单清单" style={{ marginTop: 12 }}>
                <Text type="secondary">后端返回 has_rebill_link=true，但 rebill_list 为空（可能是返账单模块权限不足）</Text>
              </Card>
            ) : null}
          </>
        )}
      </Modal>

      {/* 回复处理进度 Modal */}
      <Modal
        title="回复最新处理进度"
        open={replyOpen}
        onCancel={() => setReplyOpen(false)}
        onOk={handleReplySubmit}
        okText="提交回复"
        cancelText="取消"
        confirmLoading={replySubmitting}
        width={600}
        centered
        destroyOnClose
      >
        <Input.TextArea
          rows={5}
          value={replyContent}
          onChange={(e) => setReplyContent(e.target.value)}
          onPaste={handleReplyPaste}
          placeholder="输入最新处理进度，可直接 Ctrl/⌘+V 粘贴图片或文件"
        />
        <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>
          直接 Ctrl/⌘+V 粘贴图片或文件{replyUploading && <Spin size="small" style={{ marginInlineStart: 8 }} />}
        </div>
        {replyImages.length > 0 && (
          <Image.PreviewGroup>
            <Space wrap size={8} style={{ marginTop: 8 }}>
              {replyImages.map((a) => (
                <div key={a.url} style={{ position: 'relative', width: 72, height: 72 }}>
                  {a.isImage ? (
                    <Image src={a.url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }} />
                  ) : (
                    <a href={a.url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, background: '#f5f5f5', borderRadius: 4, fontSize: 12, color: '#1890ff', textDecoration: 'none', wordBreak: 'break-all', padding: 4, textAlign: 'center' }}>
                      {a.name}
                    </a>
                  )}
                  <Button
                    size="small" danger type="text" icon={<DeleteOutlined />} title="删除"
                    onClick={() => removeReplyImage(a.url)}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                  />
                </div>
              ))}
            </Space>
          </Image.PreviewGroup>
        )}
      </Modal>

      {/* 开始处理：实际操作内容（必填）+ 结果反馈（可选），提交后流转到申诉处理中 */}
      <Modal title="开始处理" open={startModalOpen} onCancel={() => setStartModalOpen(false)} width={720} footer={null}>
        <p style={{ color: '#666', marginBottom: 16 }}>
          记录实际操作内容后，工单将自动流转到「申诉处理中工单」。
        </p>
        <Form form={startForm} layout="vertical" onFinish={handleStartSubmit}>
          <Form.Item name="actual_action" label="实际操作内容" rules={[{ required: true, message: '请填写实际操作内容' }]}>
            <Input.TextArea rows={5} placeholder="如：已联系渠道方核实账单、已发送申诉邮件催复、已提交申诉材料等" />
          </Form.Item>
          <Form.Item name="result_feedback" label="结果反馈（可选）">
            <Input.TextArea rows={3} placeholder="操作后的结果和反馈" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={startSubmitting}>保存并流转</Button>
              <Button onClick={() => setStartModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 流转至待客服处理：实际操作记录 + 结果反馈（必填，与编辑页同一字段） */}
      <Modal title="流转至待客服处理" open={resultModalOpen} onCancel={() => setResultModalOpen(false)} width={720} footer={null} destroyOnClose>
        <p style={{ color: '#666', marginBottom: 16 }}>
          请填写本轮「实际操作记录」和「结果反馈」，提交后工单流转至「待客服处理」，由客服/销售查看并回复跟进意见。
        </p>
        <Form form={resultForm} layout="vertical" onFinish={handleToAwaiting}>
          <Form.Item name="actual_action" label="实际操作记录" rules={[{ required: true, message: '请填写实际操作记录后再流转给客服' }]}>
            <Input.TextArea rows={4} placeholder="如：已联系渠道方核实、已发送邮件催复、已提交申诉材料等" />
          </Form.Item>
          <Form.Item name="result_note" label="结果反馈" rules={[{ required: true, message: '请填写结果反馈后再流转给客服' }]}>
            <Input.TextArea rows={4} placeholder="如：渠道方已确认处理结果、申诉进展、最终减免结果等" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">提交并流转给客服</Button>
              <Button onClick={() => setResultModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 退回申诉待跟进 */}
      <Modal title="退回申诉待跟进" open={backModalOpen} onCancel={() => setBackModalOpen(false)} width={600} footer={null} destroyOnClose>
        <p style={{ color: '#666', marginBottom: 16 }}>请填写本轮实际进展，提交后工单退回「申诉待跟进」，待补齐结果后再流转给客服。</p>
        <Form form={backForm} layout="vertical" onFinish={handleBackToPending}>
          <Form.Item name="progress_note" label="实际进展内容" rules={[{ required: true, message: '请填写进展内容后再退回待跟进' }]}>
            <Input.TextArea rows={4} placeholder="如：已联系渠道方等待回复、资料补充中，暂无法给出最终结果" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">确认退回</Button>
              <Button onClick={() => setBackModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 客服回复（待客服处理 → 申诉待跟进） */}
      <Modal title="客服/销售回复" open={custReplyOpen} onCancel={() => setCustReplyOpen(false)} width={600} footer={null} destroyOnClose>
        <p style={{ color: '#666', marginBottom: 16 }}>请填写跟进处理意见，提交后工单回到「申诉待跟进」继续跟进。</p>
        <Form form={custReplyForm} layout="vertical" onFinish={handleCustomerReply}>
          <Form.Item name="reply_note" label="跟进处理意见" rules={[{ required: true, message: '请填写跟进处理意见' }]}>
            <Input.TextArea rows={4} placeholder="如：已与客户沟通确认申诉结果、客户已接受减免方案" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">提交回复</Button>
              <Button onClick={() => setCustReplyOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 重新打开（完成 → 申诉待跟进） */}
      <Modal title="重新打开申诉工单" open={reopenOpen} onCancel={() => setReopenOpen(false)} width={600} footer={null} destroyOnClose>
        <p style={{ color: '#666', marginBottom: 16 }}>请填写新的工单需求，提交后工单回到「申诉待跟进」重新流转。</p>
        <Form form={reopenForm} layout="vertical" onFinish={handleReopenSubmit}>
          <Form.Item name="new_issue" label="新的工单需求" rules={[{ required: true, message: '请填写新的工单需求后再重新打开' }]}>
            <Input.TextArea rows={4} placeholder="如：客户追加申诉新的账单条目，需重新跟进" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">确认重新打开</Button>
              <Button onClick={() => setReopenOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改客户 Modal */}
      <Modal
        title={current?.customer_name ? '修改客户' : '添加客户'}
        open={customerModalOpen}
        onCancel={() => setCustomerModalOpen(false)}
        onOk={handleCustomerSave}
        okText="保存"
        cancelText="取消"
        confirmLoading={customerEditSaving}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#666' }}>
          当前工单客户：<strong>{current?.customer_name || '(未填)'}</strong>
        </div>
        <Select
          id="appeal-customer-select"
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
          placeholder="请选择客户"
          value={selectedCustomer}
          onChange={(v) => setSelectedCustomer(v)}
          options={customerList.map((c) => ({ value: c.name, label: c.name }))}
        />
      </Modal>

      {/* 编辑 Drawer */}
      <Drawer
        title="编辑申诉工单"
        open={!!editing}
        onClose={() => setEditing(null)}
        width={560}
      >
        <Form form={editForm} layout="vertical" requiredMark={false}>
          <Form.Item name="appeal_reason" label="申诉原因">
            <Select options={REASON_OPTIONS} />
          </Form.Item>
          <Form.Item name="appeal_amount" label="申诉金额">
            <Input placeholder="如 $150" />
          </Form.Item>
          <Form.Item name="customer_name" label="客户" rules={[{ required: true, message: '请选择客户' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="customer_owner" label="客户归属" rules={[{ required: true, message: '请选择客户归属' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="expected_result" label="客户预期可接受结果">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="channel_party" label="渠道方">
            <Input />
          </Form.Item>
          <Form.Item name="follow_strategy" label="遵循系统优先级规则">
            <Select options={[{ value: 'yes', label: '是' }, { value: 'no', label: '否' }]} />
          </Form.Item>
          <Form.Item name="escalate_hours" label="多久未处理升级紧急">
            <Select allowClear options={[
              { value: '12h', label: '12小时' }, { value: '1d', label: '1天' },
              { value: '3d', label: '3天' }, { value: '5d', label: '5天' }, { value: '10d', label: '10天' },
            ]} />
          </Form.Item>
          <Form.Item name="actual_action" label="实际操作记录">
            <Input.TextArea rows={3} placeholder="填写后可在列表中直接「流转待客服」" />
          </Form.Item>
          <Form.Item name="result_feedback" label="结果反馈">
            <Input.TextArea rows={3} placeholder="填写后可在列表中直接「流转待客服」" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" loading={editSaving} onClick={handleEditSave}>保存</Button>
              <Button onClick={() => setEditing(null)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
